/** @file API logic for billing calls */

import changeCase from 'change-object-case';
import { toast } from 'react-toastify';

import { StructureBilling } from '@/Structures/types';

import { createDefaultHeaders, getJSONOrThrow, throwIfError } from '../api';
import { getSession, urlWithAdminQuery } from '../auth';
import { log } from '../util';
import { Bill } from './types';

// Constants //////////////////////////////////////////////////////////////////

const SEND_BILLS_DEFAULT_DAYS_UNTIL_DUE = 21;

// Utility ////////////////////////////////////////////////////////////////////

/**
 * Process server response from get/list/update billing
 *
 * @param  {object} unNormalizedData - Server response
 * @returns {object} - Passed in object
 */
const handleBillingResponse = (unNormalizedData): any => {
  const camelCased = changeCase.camelArray(unNormalizedData, {
    recursive: true,
    arrayRecursive: true,
  });
  return camelCased;
};

// API Calls //////////////////////////////////////////////////////////////////

// Create /////////////////////////////
type CreateObj = {
  start: number; // 1598922000,
  end: number; // 1601427600,
  volume: number; // 12345.05,
  volume_units: string;
  cost: number; // 54321.01,
  structureId: string; // "8f9bb99f-9ecc-4893-9db4-909188526d6a"
  rate_method: string;
  effective_rate: number | null;
  fees: { name: string; feeType: string; cost: number; unitId?: string }[];
  // eUsage: {
  //   deviceId: string;
  //   usage: string;
  // }[];
};

/**
 * Create bill
 *
 * @param  {object} createObj - Data to create bill
 * @returns {Promise} - Result of call to create bill
 */
export const create = (createObj: CreateObj): any => {
  let lineItems: any[] = [
    {
      type: 'USAGE',
      volume: createObj.volume,
      volume_units: createObj.volume_units,
      rate_method: createObj.rate_method,
      effective_rate: createObj.effective_rate,
      cost: createObj.cost,
      cost_units: 'DOLLARS',
      description: 'Volume Usage',
    },
  ];

  for (const key in createObj.fees) {
    if (createObj.fees[key].feeType === 'UNIT_FLAT') {
      lineItems.push({
        type: createObj.fees[key].feeType,
        description: createObj.fees[key].name,
        cost: Number(createObj.fees[key].cost),
        cost_units: 'DOLLARS',
        unit_id: createObj.fees[key].unitId,
      });
    } else
      lineItems.push({
        type: createObj.fees[key].feeType,
        description: createObj.fees[key].name,
        cost: Number(createObj.fees[key].cost),
        cost_units: 'DOLLARS',
      });
  }

  // for (const key in createObj.eUsage) {
  //   lineItems.push({
  //     type: 'ESTIMATED_USAGE',
  //     description: 'Estimated Usage',
  //     volume: Number(createObj.eUsage[key].usage),
  //     volume_units: 'GALLONS',
  //     rate_method: createObj.rate_method,
  //     effective_rate: createObj.effective_rate,
  //     cost_units: 'DOLLARS',
  //     device_id: createObj.eUsage[key].deviceId,
  //     start_date: dayjs.unix(createObj.start).format('YYYY-MM-DD'),
  //     end_date: dayjs.unix(createObj.end).format('YYYY-MM-DD'),
  //   });
  // }

  return getSession().then((session) =>
    fetch(urlWithAdminQuery('/billing', session.adminEmail), {
      method: 'POST',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
      body: JSON.stringify({
        start_date: createObj.start,
        end_date: createObj.end,
        volume: createObj.volume,
        volume_units: createObj.volume_units,
        cost: createObj.cost,
        cost_units: 'DOLLARS',
        rate_method: createObj.rate_method,
        structure_id: createObj.structureId,
        effective_rate: createObj.effective_rate,
        line_items: lineItems,
      }),
    })
      .then((resp) => resp.json())
      .catch((err) => {
        console.error(err);
        toast.error(`Error: Create billing for Property: ${createObj.structureId}`, {
          closeButton: true,
        });
        return err;
      }),
  );
};

// Get ////////////////////////////////

/**
 * Get bills for structure
 *
 * @param  {string} structureId - ID for specififc structure to get bills for
 * @returns {Promise} - Result of call to get bill
 */
export const getForStructure = (structureId: string): Promise<Bill[]> => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery(`/billing/${structureId}`, session.adminEmail), {
      method: 'GET',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
    })
      .then((resp) => resp.json())
      .then(handleBillingResponse)
      .catch((err) => {
        console.error(err);
        toast.error(`Error: Getting billings for Property: ${structureId}`, { closeButton: true });
        return err;
      }),
  );
};

// Edit ///////////////////////////////

type EditObj = CreateObj & { id: string };

/**
 * Edit utility bill
 *
 * @param  {object} editObj - Data to edit utility bill
 * @returns {Promise} - Result of call to edit utility bill
 */
export const edit = (editObj: EditObj) => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery(`/billing/${editObj.id}`, session.adminEmail), {
      method: 'PUT',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
      body: JSON.stringify({
        start_date: editObj.start,
        end_date: editObj.end,
        volume: editObj.volume,
        volume_units: editObj.volume_units,
        cost: editObj.cost,
        cost_units: 'DOLLARS',
        rate_method: editObj.rate_method,
        structure_id: editObj.structureId,
        effective_rate: editObj.effective_rate,
      }),
    })
      .then((resp) => resp.json())
      .catch((err) => {
        console.error(err);
        toast.error(`Error: Editing billings for Property: ${editObj.structureId}`, {
          closeButton: true,
        });
        return err;
      }),
  );
};

// Remove /////////////////////////////////////////////////////////////////////

/**
 * Remove Bill
 *
 * @param  {string} billId - Bill ID
 * @returns {Promise} - Result of call to remove bill
 */
export const remove = (billId: string): Promise<void> => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery(`/billing/${billId}`, session.adminEmail), {
      method: 'DELETE',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
    }).catch((err) => {
      console.error(err);
      toast.error(`Error: Deleting billings for id: ${billId}`, { closeButton: true });
      return err;
    }),
  );
};

// Generate bill details /////////////////////////////////////////////////////////////////////

export const generateBill = (
  billId: string,
  isElasticsearchOutage: boolean,
): Promise<StructureBilling> => {
  if (isElasticsearchOutage) {
    const outageError = new Error(
      `Under maintenance! The billing generation system is currently unavailable.`,
    );
    toast.error(outageError.message, { closeButton: true });
    return Promise.reject(outageError);
  }

  return getSession().then((session) =>
    fetch(urlWithAdminQuery(`/billing/generate/${billId}`, session.adminEmail), {
      method: 'GET',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
    })
      .then(throwIfError)
      .then((resp) => resp.json())
      .catch((err) => {
        const reqId = err.headers.get('request-id');
        const newErr = new Error(`Error Generating structure billing [requestId: ${reqId}]`);
        toast.error(newErr.message, { closeButton: true });
        log.error(newErr);
        throw newErr;
      }),
  );
};

// sendBills /////////////////////////////////////////////////////////////////////

export const sendBills = (
  billId: string,
  daysUntilDue: number = SEND_BILLS_DEFAULT_DAYS_UNTIL_DUE,
): Promise<Bill> => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery(`/billing/${billId}/sendBills`, session.adminEmail), {
      method: 'POST',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
      body: JSON.stringify({
        days_until_due: daysUntilDue,
      }),
    })
      .then(getJSONOrThrow)
      .catch((err) => {
        const newErr = new Error(`Error sending bills: ${err.message}`);
        toast.error(newErr.message, { closeButton: true });
        log.error(newErr);
      }),
  );
};

// Generate Invoice PDF /////////////////////////////////////////////////////////////////////

export type GenerateInvoicePDFRequest = {
  pay_online_url?: string;
  payment_instructions?: string;
};

export type GenerateInvoicePDFResponse = {
  download_url: string;
  expires_in: number;
  s3_key: string;
  s3_bucket: string;
};

/**
 * Generate a custom invoice PDF for a specific billing invoice
 *
 * @param billingId - UUID of the Billing record
 * @param invoiceId - UUID of the Invoice within the billing
 * @param options - Optional payment URL and instructions overrides
 * @returns Promise with download URL and metadata
 */
export const generateInvoicePDF = (
  billingId: string,
  invoiceId: string,
  options?: GenerateInvoicePDFRequest,
): Promise<GenerateInvoicePDFResponse> => {
  return getSession().then((session) =>
    fetch(
      urlWithAdminQuery(`/billing/${billingId}/invoices/${invoiceId}/pdf`, session.adminEmail),
      {
        method: 'POST',
        headers: createDefaultHeaders({
          authToken: session.idToken.toString(),
        }),
        body: JSON.stringify(options || {}),
      },
    )
      .then(throwIfError)
      .then((resp) => resp.json())
      .catch((err) => {
        const reqId = err.headers?.get?.('request-id');
        const newErr = new Error(
          `Error generating invoice PDF${reqId ? ` [requestId: ${reqId}]` : ''}`,
        );
        toast.error(newErr.message, { closeButton: true });
        log.error(newErr);
        throw newErr;
      }),
  );
};

// Batch Custom Invoices /////////////////////////////////////////////////////////////////////

export type BatchCustomInvoiceInfo = {
  custom_invoice_id: string;
  billing_invoice_id: string;
  status: 'not_created' | 'created' | 'failed';
};

export type GenerateBatchCustomInvoicesRequest = {
  pay_online_url?: string;
  payment_instructions?: string;
  due_date_days?: number;
};

export type GenerateBatchCustomInvoicesResponse = {
  custom_invoices: BatchCustomInvoiceInfo[];
  total_queued: number;
};

/**
 * Trigger batch generation of custom invoice PDFs for all invoices in a billing.
 * Creates CustomInvoice records and queues them for async PDF generation.
 *
 * @param billingId - UUID of the Billing record
 * @param options - Optional payment URL and instructions overrides
 * @returns Promise with list of custom invoice records created
 */
export const generateBatchCustomInvoices = (
  billingId: string,
  options?: GenerateBatchCustomInvoicesRequest,
): Promise<GenerateBatchCustomInvoicesResponse> => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery(`/billing/${billingId}/custom-invoices/batch`, session.adminEmail), {
      method: 'POST',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
      body: JSON.stringify(options || {}),
    })
      .then(throwIfError)
      .then((resp) => resp.json())
      .catch((err) => {
        const reqId = err.headers?.get?.('request-id');
        const newErr = new Error(
          `Error generating batch custom invoices${reqId ? ` [requestId: ${reqId}]` : ''}`,
        );
        toast.error(newErr.message, { closeButton: true });
        log.error(newErr);
        throw newErr;
      }),
  );
};

export type CustomInvoiceDownloadInfo = {
  id: string | null;
  billing_invoice_id: string;
  download_url: string | null;
  status: 'not_found' | 'invalid' | 'not_created' | 'created' | 'failed';
};

export type GetCustomInvoiceDownloadUrlsRequest = {
  billing_invoice_ids: string[];
};

export type GetCustomInvoiceDownloadUrlsResponse = {
  custom_invoices: CustomInvoiceDownloadInfo[];
  ready_count: number;
  not_ready_count: number;
  expires_in: number;
};

/**
 * Get presigned download URLs for custom invoices by their billing invoice IDs.
 * Looks up the most recent CustomInvoice for each billing invoice.
 * Only invoices with status 'created' will have download URLs.
 *
 * @param billingId - UUID of the Billing record
 * @param billingInvoiceIds - Array of BillingInvoice UUIDs to get custom invoice URLs for
 * @returns Promise with download URLs and status for each invoice
 */
export const getCustomInvoiceDownloadUrls = (
  billingId: string,
  billingInvoiceIds: string[],
): Promise<GetCustomInvoiceDownloadUrlsResponse> => {
  return getSession().then((session) =>
    fetch(
      urlWithAdminQuery(`/billing/${billingId}/custom-invoices/download-urls`, session.adminEmail),
      {
        method: 'POST',
        headers: createDefaultHeaders({
          authToken: session.idToken.toString(),
        }),
        body: JSON.stringify({ billing_invoice_ids: billingInvoiceIds }),
      },
    )
      .then(throwIfError)
      .then((resp) => resp.json())
      .catch((err) => {
        const reqId = err.headers?.get?.('request-id');
        const newErr = new Error(
          `Error getting custom invoice download URLs${reqId ? ` [requestId: ${reqId}]` : ''}`,
        );
        toast.error(newErr.message, { closeButton: true });
        log.error(newErr);
        throw newErr;
      }),
  );
};
